/**
 * Formatting helpers used by the API→view mappers. The API returns raw
 * timestamps and voting periods; the CMS shows human-readable values.
 */

export interface VotingPeriodDto {
  id: string;
  projectId: string;
  mode: 'scheduled' | 'manual';
  opensAt: string;
  closesAt: string | null;
}

export function hoursSince(value: string | Date): number {
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (Number.isNaN(time)) return 0;
  return Math.max(0, Math.round((Date.now() - time) / (60 * 60 * 1000)));
}

/** Compact relative time, e.g. "just now", "2h ago", "3d ago", "1w ago". */
export function relativeTime(value: string | Date): string {
  const hours = hoursSince(value);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Human summary of a voting period: Starts/Ends/Closed/—. */
export function formatVotingPeriod(
  period: VotingPeriodDto | null | undefined,
  now: Date = new Date(),
): string {
  if (!period) return '—';
  const opens = new Date(period.opensAt).getTime();
  if (now.getTime() < opens) return `Starts ${formatDate(period.opensAt)}`;
  if (period.closesAt && now.getTime() > new Date(period.closesAt).getTime()) {
    return `Closed ${formatDate(period.closesAt)}`;
  }
  if (period.closesAt) return `Ends ${formatDate(period.closesAt)}`;
  return 'Open';
}
